import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, forkJoin, map, Observable, of } from 'rxjs';

import {
    Event,
    EventFinances,
    ExternalAssociation,
    Feature,
    IdentitySeason,
    OnlineSalesSummary,
    PagedResponse,
    ParticipantCount,
    StripeAccount,
} from '@reg/shared/model';
import { ApprovalStatusService, HttpServiceHelper } from '@reg/shared/service';
import { EventSuggestion } from '@reg/event/search/event-suggestion.model';
import { ArrayHelper } from '@reg/shared/helper/array-helper';
import { endOfDay, startOfDay } from 'date-fns';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';
import { EventCore } from '../model/core/event-core.model';

export interface IEventIndexOptions {
    disableForceRefresh?: boolean;
    disableDisplaySpinner?: boolean;
    disableTwEventFilter?: boolean;
}

/**
 * Service used to get and save Event data.
 */
@Injectable()
export class EventService {
    public static INCLUDE_FEATURES: string = 'features';
    public static INCLUDE_PRODUCTS: string = 'products';
    public static INCLUDE_SCHEDULE_ITEMS: string = 'scheduleItems';
    public static INCLUDE_EXTERNAL_LINKS: string = 'externalLinks';
    public static INCLUDE_FLO_LEVELS: string = 'floLevels';
    public static INCLUDE_SEASONS: string = 'seasons';
    public static INCLUDE_CONTACTS: string = 'contacts';
    public static INCLUDE_MATS: string = 'mats';
    public static INCLUDE_DIVISIONS_W_REGISTRATION: string = 'divisions.divisionRegistration.teams.coaches';
    public static INCLUDE_DIVISIONS_W_WEIGHT_CLASSES: string = 'divisions.weightClasses';
    public static INCLUDE_CREATED_BY_USER_ACCOUNT: string = 'createdByUserAccount';
    public static INCLUDE_STRIPE_ACCOUNT: string = StripeAccount.NAME;

    get siteId(): number {
        return this.http.configService.getSportConfig('siteId');
    }

    /** Property used to store if the user has been shown the event details modal */
    public isDetailsShown: boolean = false;
    public isSetupCompleteShown: boolean = false;
    private cachedEventMap: { [apiRequest: string]: Event; } = {};
    private cachedPagedMap: { [apiRequest: string]: PagedResponse; } = {};
    private cachedHasAccessMap: { [apiRequest: string]: boolean; } = {};
    private searchPageSize: number = 50;

    constructor(private http: HttpServiceHelper) {
    }

    public getUserAdminEventsParams(pageSize: number = 50): string[] {
        return [
            'hasAccess=true',
            'isDual=false',
            `page[size]=${pageSize}`,
            `featureIds=${Feature.EVENT_MGMNT_ID},${Feature.REGISTRATION_ID},${Feature.STREAMING_ID}`,
        ];
    }

    public getEventsBySeasonIdParams(season: IdentitySeason): string[] {
        return [
            `identitySeasonId=${season.id}`,
            `approvalStatus=${ApprovalStatusService.APPROVED}`,
            'page[size]=11',
        ];
    }

    /**
     * Get the event for the specified event Id
     */
    public getIndexPagedResponse(
        includes: string[] = null,
        queryParams: string[] = null,
        options: IEventIndexOptions = {},
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(queryParams);
        const forceRefresh: boolean = !options.disableForceRefresh;
        const displaySpinner: boolean = !options.disableDisplaySpinner;

        if (!options.disableTwEventFilter) {
            // Don't fetch Trackwrestling events
            queryParams.push('hasTrackwrestlingExternalLink=false');
        }

        includes = this.appendFeatures(includes);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh && this.cachedPagedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedPagedMap[apiRequestUrl].clone());
        }

        return this.getIndexPagedResponseByUrl(apiRequestUrl, displaySpinner)
            .pipe(map((resp: PagedResponse) => {
                this.cachedPagedMap[apiRequestUrl] = resp.clone();
                return resp;
            }));
    }

    /**
     * Get wrestlers by a url. This is used for paging through wrestlers since the response
     * provides the proper page urls
     */
    public getIndexPagedResponseByUrl(url: string, displaySpinner: boolean = true): Observable<PagedResponse> {
        return this.http.get(url, displaySpinner)
            .pipe(map(this.handlePagedResponse.bind(this)));
    }

    /**
     * Get events by a list of eventIds. Since eventIds are guids (36 characters) a list of them could exceed the
     * max url string limit. Thus, we need to POST the eventIds instead.
     */
    public getByIds(
        eventIds: string[],
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<Event[]> {
        if (!eventIds || !eventIds.length) {
            return of([]);
        }

        queryParams = this.appendGetFields(queryParams);
        includes = this.appendFeatures(includes);
        const apiRequestUrl: string = this.getApiUrl('get-by-ids', includes, queryParams);

        // Break the list of eventIds into chunks of 50. We will then request each chunk in parallel
        const chunks: [string[]] = ArrayHelper.createChunks(eventIds, 50) as [string[]];

        // Build the list of requests for each chunk of eventIds
        const getRequests: Observable<Event[]>[] =
            chunks.map((chunk: string[]) => this.http.post(apiRequestUrl, { eventIds: chunk })
                .pipe(map((response: IJsonApiResponse) => {
                    const json: any = response;
                    return json.data.map((d: any) => new Event(this.siteId).deserialize(d, json.included));
                })));

        return forkJoin(getRequests)
            .pipe(map((results: [Event[]]) => {
                let allEvents: Event[] = [];
                results.forEach((events: Event[]) => { allEvents = allEvents.concat(events); });
                return allEvents;
            }));
    }

    /**
     * Convert the given response into a PagedResponse
     */
    private handlePagedResponse(response: IJsonApiResponse): PagedResponse {
        const json: any = response;
        const events: Event[] = json.data.map((d: any) => new Event(this.siteId).deserialize(d, json.included));

        return new PagedResponse(
            json.links.first,
            json.links.last,
            json.links.prev,
            json.links.next,
            json.meta.total,
            events,
        );
    }

    /**
     * Get all wrestlers for the specified event
     */
    public getIndex(includes: string[] = null, queryParams: string[] = null): Observable<Event[]> {
        queryParams = this.appendGetFields(queryParams);
        includes = this.appendFeatures(includes);

        return this.getIndexPagedResponse(includes, queryParams)
            .pipe(map((pagedResponse: PagedResponse) => pagedResponse.recordList));
    }

    /**
     * search for teams
     */
    public search(
        searchText: string = null,
        startDate: Date = null,
        endDate: Date = null,
    ): Observable<PagedResponse> {
        const orParams: string[] = [];
        const andParams: string[] = [];

        // Get the utc seconds from the start of the given startDateTime
        let startDateTimestamp: number = 0;
        let endDateTimestamp: number = 0;

        if (startDate) {
            const momentStartDate: Date = startOfDay(startDate);
            startDateTimestamp = Math.floor((momentStartDate.getTime() / 1000));
        }
        if (endDate) {
            const momentEndDate: Date = endOfDay(endDate);
            endDateTimestamp = Math.ceil((momentEndDate.getTime() / 1000));
        }

        if (startDateTimestamp && endDateTimestamp) {
            andParams.push(`(range field=start_date [${startDateTimestamp},${endDateTimestamp}])`);
        } else if (startDateTimestamp) {
            andParams.push(`(range field=start_date {${startDateTimestamp},})`);
        } else if (endDateTimestamp) {
            andParams.push(`(range field=start_date {,${endDateTimestamp}})`);
        }

        andParams.push('(term field=content_type \'arena_event\')');

        if (searchText) {
            const escapedSearchText: string = encodeURIComponent(searchText.trim().replace(/'/g, '\\\''));
            const prefix: string = `(prefix field=title boost=10 '${escapedSearchText}')`;
            orParams.push(`(term field=title boost=10 '${escapedSearchText}')`);
            orParams.push(prefix);
            const words: string[] = searchText.split(' ');

            // For each word add a "near" query
            words.forEach((word: string) => {
                const trimmedWord: string = encodeURIComponent(word.trim().replace(/'/g, '\\\''));
                if (trimmedWord) {
                    orParams.push(`(near field=title distance=4 '${trimmedWord}')`);
                }
            });
        }

        let query: string = `(and ${andParams.join(' ')}`;

        if (orParams.length > 0) {
            query += `(or ${orParams.join(' ')})`;
        }

        query += ')';
        query += '&q.parser=structured';
        query += '&sort=_score desc,start_date asc';
        query += `&size=${this.searchPageSize}`;

        let apiRequestUrl: string = `${this.getApiUrl(null, null, null)}search`;
        apiRequestUrl += this.http.getQueryParameters(null, [`q=${query}`]);

        return this.searchByUrl(apiRequestUrl);
    }

    public searchByUrl(apiRequestUrl: string = null): Observable<PagedResponse> {
        return this.http.get(apiRequestUrl, false)
            .pipe(map((response: IJsonApiResponse) => {
                const eventSuggestions: EventSuggestion[] = [];
                const responseBody: any = response;
                const hits: any = responseBody.hits;

                if (hits?.hit) {
                    for (const hit of hits.hit) {
                        eventSuggestions.push((new EventSuggestion()).fillFromSearchHit(hit));
                    }
                }

                let nextUrl: string = null;
                if (hits.cursor &&
                    (hits.start + this.searchPageSize) < hits.found) {
                    // Only set the next url if there are more results to fetch
                    nextUrl = `${apiRequestUrl}&cursor=${hits.cursor}`;
                }

                return new PagedResponse(
                    null,
                    null,
                    null,
                    nextUrl,
                    hits.found,
                    eventSuggestions,
                );
            }));
    }

    /**
     * Add or update the event
     */
    public save(event: Event, includes?: string[]): Observable<Event> {
        let response: Observable<IJsonApiResponse>;
        const queryParams: string[] = this.appendGetFields();
        includes = this.appendFeatures(includes);

        if (event.id && !event.isNew) {
            // Update existing event
            const apiRequestUrl: string = this.getApiUrl(event.id, includes, queryParams);
            response = this.http.patch(apiRequestUrl, event.serialize());
        } else {
            // Add a new event
            const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
            response = this.http.post(apiRequestUrl, event.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => new Event(this.siteId).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Get the event for the specific event Id
     */
    public getIsOnlineRegistrationOpen(eventId: string): Observable<IOnlineRegistrationOpen> {
        const timeZoneOffset: number = (new Date()).getTimezoneOffset() / 60;
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/is-online-registration-open/${timeZoneOffset}`;

        return this.http.get<IOnlineRegistrationOpen>(apiRequestUrl)
            .pipe(map((response: IOnlineRegistrationOpen) => response));
    }

    /**
     * Get the event for the specified event Id
     */
    public getIsCollectingMoneyOnline(eventId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/is-collecting-money-online`;

        return this.http.get<boolean>(apiRequestUrl).pipe(map((response: boolean) => response));
    }

    /**
     * Get the event finances
     */
    public getFinances(eventId: string): Observable<EventFinances> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/finances`;
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => new EventFinances().deserialize(response)));
    }

    /**
     * Get the event for the specified event Id
     */
    public get(
        eventId: string,
        includes?: string[],
        queryParams: string[] = [],
        forceRefresh: boolean = false,
        displaySpinner: boolean = true,
    ): Observable<Event> {
        queryParams = this.appendGetFields(queryParams);
        includes = this.appendFeatures(includes);
        const apiRequestUrl: string = this.getApiUrl(eventId, includes, queryParams);

        if (!forceRefresh &&
            this.cachedEventMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedEventMap[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl, displaySpinner)
            .pipe(map((response: IJsonApiResponse) => {
                const event: Event = new Event(this.siteId).deserialize(response);
                this.cachedEventMap[apiRequestUrl] = event.clone();
                return event;
            }));
    }

    /**
     * Get the date time of when result emails will be auto sent for the event
     */
    public getResultEmailAutoSendDateTime(eventId: string, displaySpinner: boolean = false): Observable<Date> {
        const apiRequestUrl: string = this.getApiUrl(`${eventId}/result-email-auto-send-date-time`);

        return this.http.get<IAutoSendDateTime>(apiRequestUrl, displaySpinner)
            .pipe(map((response: IAutoSendDateTime) => new Date(response.autoSendDateTime)));
    }

    /**
     * Get sales summary for a given event
     */
    public getOnlineSalesSummary(eventId: string): Observable<OnlineSalesSummary> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/online-sales-summary`;

        return this.http.get(apiRequestUrl, false, false)
            .pipe(
                map((response: IJsonApiResponse) => (new OnlineSalesSummary()).deserialize(response)),
                catchError((error: HttpErrorResponse) => this.http.handleError(error, null)),
            );
    }

    /**
     * Transfer funds to the event's configured stripe managed account's bank account
     */
    public emailResults(eventId: string, teamContactId: string = null): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/email-results`;
        const body: any = {
            data: {
                attributes: {
                    teamContactId: teamContactId,
                },
            },
        };

        return this.http.post(apiRequestUrl, body)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Send email notifications
     */
    public sendNotifications(eventId: string, notificationType: string, toEmails: string[]): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/notifications`;

        const body: any = {};
        body.data = {};
        body.data.attributes = {};
        body.data.attributes.notificationType = notificationType;
        body.data.attributes.sendToEmails = toEmails;

        return this.http.post(apiRequestUrl, body).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Get features based on the current sport
     */
    public getFeatures(queryParams: string[] = null): Observable<Feature[]> {
        let apiUrl: string = `${this.http.configService.getApiBaseUrl()}/features/`;
        apiUrl += this.http.getQueryParameters(null, queryParams);

        return this.http.get(apiUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const features: Feature[] = [];
                const bodyData: any = response.data || [];
                for (const feature of bodyData) {
                    features.push((new Feature()).deserialize(feature));
                }
                return features;
            }));
    }

    /**
     * Get the count of participants
     */
    public getParticipantCount(eventId: string): Observable<ParticipantCount> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/wrestler-count`;

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new ParticipantCount()).deserialize(response)));
    }

    /**
     * Get the count of order transactions
     */
    public getOrderTransactionCount(eventId: string): Observable<number> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/order-transaction-count`;

        return this.http.get<IOrderTransactionCount>(apiRequestUrl)
            .pipe(map((response: IOrderTransactionCount) => response.count));
    }

    /**
     * Export event data
     */
    public exportData(eventId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(`${eventId}/export-sql-data`);
        return this.http.post(apiRequestUrl, null).pipe(map(() => true));
    }

    /**
     * Get if the user has access to the given event
     */
    public getHasAccess(eventId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, [])}/user-access`;

        if (this.cachedHasAccessMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedHasAccessMap[apiRequestUrl]);
        }
        return this.http.get(apiRequestUrl, true, false)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const hasAccess: boolean = response['hasAccess'];
                    this.cachedHasAccessMap[apiRequestUrl] = hasAccess;
                    return hasAccess;
                }),
                catchError((error: HttpErrorResponse) => {
                    this.http.handleError(error, this.http.toastService);
                    return of(false);
                }),
            );
    }

    /**
     * Get the event description for the specified event Id
     */
    public getDescription(eventId: string): Observable<string> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, ['fields[event]=description']);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const data: Object = response['data'];
                if (data['attributes']) {
                    return data['attributes']['description'];
                }

                return null;
            }));
    }

    /**
     * Copy an event
     */
    public clone(
        event: Event,
        stripeAccountId: string,
        options: string[] = [],
        includes: string[] = null,
    ): Observable<Event> {
        const queryParams: string[] = this.appendGetFields();
        includes = this.appendFeatures(includes);

        const body: any = {
            data: {
                type: 'event',
                attributes: {
                    name: event.name.trim(),
                    startDateTimeUtc: event.startDateTime.toISOString(),
                    endDateTimeUtc: event.endDateTime.toISOString(),
                    cloneOptions: options,
                },
                relationships: {
                    stripeAccount: { type: 'stripeAccount', id: stripeAccountId },
                },
            },
        };

        const apiRequestUrl: string = this.getApiUrl(`${event.id}/clone`, includes, queryParams);
        return this.http.post(apiRequestUrl, JSON.stringify(body))
            .pipe(
                map((response: IJsonApiResponse) => new Event(this.siteId).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    public exportSeedingCriteria(eventId: string, queryParams: string[] = null): void {
        location.href = this.getApiUrl(`${eventId}/export-seeding-criteria`, null, null);
    }

    private clearCache(): void {
        this.cachedEventMap = {};
        this.cachedPagedMap = {};
    }

    private appendFeatures(includes: string[] = []): string[] {
        includes = includes || [];
        // Most event get calls include the features so here we will always include features.
        // This actually helps reduce the number of event requests we make because the cache will almost
        // always have a url that includes the features.
        if (includes.filter((x: string) => x.indexOf(EventService.INCLUDE_FEATURES) !== -1).length === 0) {
            includes.push(EventService.INCLUDE_FEATURES);
        }

        return includes;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.lastIndexOf('fields[event]') !== -1).length === 0) {
            queryParams.push(`fields[event]=${Event.getFields().join(',')}`);
        }

        return queryParams;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/`;

        if (eventId) {
            url += eventId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    public getFloEventHubUrl(eventId: string): string {
        const url: string =
            `${this.http.configService.getFloEventHubBaseUrl()}/cms/events/${eventId}/edit?status=0,1&future=1`;

        return url;
    }
}

export interface IOnlineRegistrationOpen {
    isOpen: boolean,
    reason: string
}
export interface IAutoSendDateTime {
    autoSendDateTime: Date;
}

export interface IOrderTransactionCount {
    count: number
}
